// Admin's addresses, which screen each draws, and who may open it (design 5.5, Team; CLAUDE.md's
// roles). The menu is the boards' (AdminToday.dc.html and the rest): Today, People, Products,
// Recordings and dates, Resources and files, Price map, then Member side; Reports and Audit log are
// added for owner and admin, as BUILD-PLAN stage 5 asks and no board draws, Masterminds after People
// for owner and admin (docs/mastermind-admin-plan.md 3.1), and Referrals (decision 55, 13 Oct: the ledger,
// who can refer what and the offers; owner and admin only) between Price map and Reports.
//
// - Owner and admin open everything. The screens decide what's owner only inside them (mapping a
//   price, deleting someone, the masterclass offer, 1-2-1 spaces).
// - Support: Today, People and a person, Recordings and dates (paste recordings, take registers) and
//   Webinars. No products, resources, billing, reports or audit log.
// - A host: Today, People and a person, and Recordings and dates for their registers (Cohorts and
//   dates; the Recordings tab sends them there), each showing only their product's (the reads scope them).

export const ROLES = ['owner', 'admin', 'support', 'host']
export const ROLE_NAMES = { owner: 'Owner', admin: 'Admin', support: 'Support', host: 'Host' }

const ALL = ROLES
const OWNER_ADMIN = ['owner', 'admin']

// Each screen: its file in src/admin/screens/, its title (the holding panel's heading and the tab's
// title), the menu item it lights up, and who may open it
export const SCREENS = {
  Today: { title: 'Today', nav: 'today', roles: ALL },
  People: { title: 'People', nav: 'people', roles: ALL },
  Person: { title: 'Person', nav: 'people', roles: ALL },
  Masterminds: { title: 'Masterminds', nav: 'masterminds', roles: OWNER_ADMIN },
  Products: { title: 'Products', nav: 'products', roles: OWNER_ADMIN },
  AddProduct: { title: 'Add a product', nav: 'products', roles: OWNER_ADMIN },
  Product: { title: 'Product settings', nav: 'products', roles: OWNER_ADMIN },
  Layout: { title: 'Page layout', nav: 'products', roles: OWNER_ADMIN },
  Recordings: { title: 'Recordings and dates', nav: 'recordings', roles: ALL },
  Webinars: { title: 'Recordings and dates', nav: 'recordings', roles: ['owner', 'admin', 'support'] },
  Resources: { title: 'Resources and files', nav: 'resources', roles: OWNER_ADMIN },
  Prices: { title: 'Price map', nav: 'prices', roles: OWNER_ADMIN },
  Referrals: { title: 'Referrals', nav: 'referrals', roles: OWNER_ADMIN },
  Reports: { title: 'Reports', nav: 'reports', roles: OWNER_ADMIN },
  Audit: { title: 'Audit log', nav: 'audit', roles: OWNER_ADMIN },
}
export const SCREEN_NAMES = Object.keys(SCREENS)

// The menu, in the boards' order. icon names are shell/Icons.jsx's.
export const NAV = [
  { key: 'today', label: 'Today', to: '/admin', icon: 'calendar' },
  { key: 'people', label: 'People', to: '/admin/people', icon: 'people' },
  { key: 'masterminds', label: 'Masterminds', to: '/admin/masterminds', icon: 'target' },
  { key: 'products', label: 'Products', to: '/admin/products', icon: 'grid' },
  { key: 'recordings', label: 'Recordings and dates', to: '/admin/recordings', icon: 'video' },
  { key: 'resources', label: 'Resources and files', to: '/admin/resources', icon: 'folder' },
  { key: 'prices', label: 'Price map', to: '/admin/prices', icon: 'tag' },
  { key: 'referrals', label: 'Referrals', to: '/admin/referrals', icon: 'people' },
  { key: 'reports', label: 'Reports', to: '/admin/reports', icon: 'chart' },
  { key: 'audit', label: 'Audit log', to: '/admin/audit', icon: 'list' },
]
export const MEMBER_SIDE = { key: 'member', label: 'Member side', to: '/', icon: 'eye' }

// Who sees which menu item: the screen it opens must be theirs
const NAV_SCREEN = { today: 'Today', people: 'People', masterminds: 'Masterminds', products: 'Products', recordings: 'Recordings', resources: 'Resources', prices: 'Prices', referrals: 'Referrals', reports: 'Reports', audit: 'Audit' }
export function navFor(role) {
  return NAV.filter(item => canOpen(role, NAV_SCREEN[item.key]))
    // a host's part of Recordings and dates is the registers, under Cohorts and dates
    .map(item => (role === 'host' && item.key === 'recordings' ? { ...item, to: '/admin/recordings/dates' } : item))
}

// An address a role is sent on from: a host's Recordings tab is the registers under Cohorts and dates
export function redirectFor(role, route) {
  if (role === 'host' && route.screen === 'Recordings' && route.params.tab === 'recordings') return '/admin/recordings/dates'
  return null
}

export function canOpen(role, screen) {
  return Boolean(SCREENS[screen]?.roles.includes(role))
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const SLUG = /^[a-z0-9][a-z0-9-]{0,63}$/

// An admin address as { screen, params, parts, path }, or { screen: null } for one that isn't admin's.
// /admin                              Today
// /admin/people                       People
// /admin/people/<person id>           Person      params.id
// /admin/masterminds[/<slug>[/<person id>[/log]]]
//                                     Masterminds params.slug, params.person, params.tab 'month' | 'log'
// /admin/products                     Products
// /admin/products/new                 AddProduct
// /admin/products/<slug>              Product     params.slug
// /admin/products/<slug>/layout       Layout      params.slug
// /admin/recordings                   Recordings  params.tab 'recordings'
// /admin/recordings/dates[/...]       Recordings  params.tab 'dates', params.rest (a session's register,
//                                                 /admin/recordings/dates/<session id>, is the screen's own)
// /admin/webinars[/<webinar id>]      Webinars    params.id
// /admin/webinars/<id>/registrations  Webinars    params.id, params.tab 'registrations' (who registered on its
//                                                 register page: owner and admin)
// /admin/resources                    Resources
// /admin/prices                       Prices
// /admin/referrals                    Referrals   params.tab 'introductions' (?line=<id> picks out a line)
// /admin/referrals/who                Referrals   params.tab 'who' (?product=<id> picks the product)
// /admin/referrals/offers             Referrals   params.tab 'offers'
// /admin/reports[/<report>]           Reports     params.report
// /admin/audit                        Audit
export function parseAdminRoute(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/'
  const parts = path.split('/').filter(Boolean)
  const out = (screen, params = {}) => ({ screen, params, parts, path })
  if (parts[0] !== 'admin') return out(null)
  const [, a, b, c, ...more] = parts
  if (!a) return out('Today')
  switch (a) {
    case 'people':
      if (!b) return out('People')
      if (UUID.test(b) && !c) return out('Person', { id: b.toLowerCase() })
      return out(null)
    case 'masterminds':
      if (!b) return out('Masterminds', {})
      if (!SLUG.test(b)) return out(null)
      if (!c) return out('Masterminds', { slug: b })
      if (!UUID.test(c)) return out(null)
      if (!more.length) return out('Masterminds', { slug: b, person: c.toLowerCase(), tab: 'month' })
      if (more.length === 1 && more[0] === 'log') return out('Masterminds', { slug: b, person: c.toLowerCase(), tab: 'log' })
      return out(null)
    case 'products':
      if (!b) return out('Products')
      if (b === 'new' && !c) return out('AddProduct')
      if (SLUG.test(b) && !c) return out('Product', { slug: b })
      if (SLUG.test(b) && c === 'layout' && !more.length) return out('Layout', { slug: b })
      return out(null)
    case 'recordings':
      if (!b) return out('Recordings', { tab: 'recordings', rest: [] })
      if (b === 'dates') return out('Recordings', { tab: 'dates', rest: [c, ...more].filter(Boolean) })
      return out(null)
    case 'webinars':
      if (!b) return out('Webinars', {})
      if (UUID.test(b) && !c) return out('Webinars', { id: b.toLowerCase() })
      if (UUID.test(b) && c === 'registrations' && !more.length) return out('Webinars', { id: b.toLowerCase(), tab: 'registrations' })
      // the property strategy tool's completions (Stage 2, docs/property-strategy-spec.md S2.9)
      if (b === 'strategy-tool' && !c) return out('Webinars', { tab: 'strategy' })
      return out(null)
    case 'resources': return b ? out(null) : out('Resources')
    case 'prices': return b ? out(null) : out('Prices')
    case 'referrals':
      if (!b) return out('Referrals', { tab: 'introductions' })
      if (b === 'who' && !c) return out('Referrals', { tab: 'who' })
      if (b === 'offers' && !c) return out('Referrals', { tab: 'offers' })
      return out(null)
    case 'reports':
      if (!b) return out('Reports', {})
      if (SLUG.test(b) && !c) return out('Reports', { report: b })
      return out(null)
    case 'audit': return b ? out(null) : out('Audit')
    default: return out(null)
  }
}

// A person's page, a product's, its layout: the addresses screens link to
export const adminPath = {
  today: () => '/admin',
  people: () => '/admin/people',
  person: id => `/admin/people/${id}`,
  masterminds: () => '/admin/masterminds',
  mastermind: (slug, query) => `/admin/masterminds/${slug}${query ? `?${query}` : ''}`,
  mmMember: (slug, personId, query) => `/admin/masterminds/${slug}/${personId}${query ? `?${query}` : ''}`,
  mmLog: (slug, personId, query) => `/admin/masterminds/${slug}/${personId}/log${query ? `?${query}` : ''}`,
  products: () => '/admin/products',
  addProduct: () => '/admin/products/new',
  product: slug => `/admin/products/${slug}`,
  layout: slug => `/admin/products/${slug}/layout`,
  recordings: () => '/admin/recordings',
  dates: () => '/admin/recordings/dates',
  register: sessionId => `/admin/recordings/dates/${sessionId}`,
  webinars: () => '/admin/webinars',
  webinar: id => `/admin/webinars/${id}`,
  webinarRegistrations: id => `/admin/webinars/${id}/registrations`,
  strategyCompletions: () => '/admin/webinars/strategy-tool',
  resources: () => '/admin/resources',
  prices: () => '/admin/prices',
  referrals: () => '/admin/referrals',
  referralsWho: productId => (productId ? `/admin/referrals/who?product=${productId}` : '/admin/referrals/who'),
  referralsOffers: () => '/admin/referrals/offers',
  referralLine: id => `/admin/referrals?line=${id}`,
  reports: () => '/admin/reports',
  report: key => `/admin/reports/${key}`,
  audit: () => '/admin/audit',
}
